import { useEffect, useRef, useState } from "react";
import { DCarousel, DCarouselInternalData, DCarouselRef } from "@dfa/ui-platform";
import { Card } from "./types/card-carousel-types";
import CarouselNavigation from "./CarouselNavigation";
import FeaturedCard from "./cards/FeaturedCard";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";
import {
	featuredResponsiveCards,
	oneToOneResponsiveCards
} from "./settings/content-carousel-settings";
import StandardCard from "./cards/StandardCard";
import { CaretRight } from "../utility/images/CaretRight";

type FeaturedCardCarouselData = {
	data: {
		cardTitle: string;
		containerCTA: {
			text: string;
			link: string;
		};
		isOneToOne: boolean;
		cards: Card[];
	};
};

const FeaturedCardCarousel = ({ data }: FeaturedCardCarouselData) => {
	const [internalCarouselData, setInternalCarouselData] = useState<DCarouselInternalData>();
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const carouselRef = useRef<DCarouselRef>(null);
	const { isOneToOne, cards, containerCTA } = data;
	const cardsLength = cards.length;
	const { XS } = SCREEN_WIDTHS;

	useEffect(() => {
		const screenSize = () => {
			setIsMobile(() => window.innerWidth <= XS);
		};
		window.addEventListener("resize", screenSize);
		screenSize();
		return () => {
			window.removeEventListener("resize", screenSize);
		};
	}, [XS, cardsLength, isOneToOne]);

	return (
		<div className={`card-carousel-container ${isOneToOne ? "one-to-one-cards" : ""}`}>
			<div
				className={`carousel-card-section-title ${isOneToOne ? "one-to-one" : ""} t-label-sm-heavy tc-cool-gray-02`}
			>
				{data.cardTitle}
			</div>
			<DCarousel
				ref={carouselRef}
				setCarouselInternal={setInternalCarouselData}
				responsiveSettings={isOneToOne ? oneToOneResponsiveCards : featuredResponsiveCards}
				settings={
					!isOneToOne
						? {
								cardsPerSection: 4,
								cardsToScroll: 3,
								draggable: true,
								gapBetweenCards: "2rem",
								prettyWrapping: true
							}
						: {
								cardsPerSection: 2,
								cardsToScroll: 1,
								draggable: true,
								gapBetweenCards: "2rem",
								prettyWrapping: true
							}
				}
			>
				{data.cards.map((card, i) => {
					if (isMobile) {
						return <StandardCard key={"card-carousel" + i} card={card} />;
					}
					return i === 0 ? (
						<FeaturedCard key={`card-carousel-${card.title}-${i}`} customSpan="2" card={card} />
					) : (
						<StandardCard key={"card-carousel" + i} card={card} />
					);
				})}
			</DCarousel>
			<div className="carousel-card-featured-utility">
				{internalCarouselData && internalCarouselData.totalSections > 0 ? (
					<CarouselNavigation
						carouselRef={carouselRef}
						clickNumber={internalCarouselData.clickNumber}
						clicksToEnd={internalCarouselData.clicksToEnd}
						currentSection={internalCarouselData.currentSection}
						totalSections={internalCarouselData.totalSections}
					/>
				) : null}
				{containerCTA.link && (
					<div
						className="carousel-card-link"
						onClick={() => {
							window.location.href = containerCTA.link;
						}}
						role="button"
						tabIndex={0}
						onKeyUp={(event): void => {
							const allowedKeys = new Set(["Enter", "Space"]);
							if (!allowedKeys.has(event.code)) return;
							event.preventDefault();
							window.location.href = containerCTA.link;
						}}
						data-a-evt="Link Click"
						data-a-name={containerCTA.text}
						data-a-comp="Card Carousel"
						data-qa={`carousel-card-link-featured${isOneToOne ? "-one-to-one" : ""}`}
					>
						<div className="carousel-card-link-text t-body-xs">{containerCTA.text}</div>
						<CaretRight />
					</div>
				)}
			</div>
		</div>
	);
};
export default FeaturedCardCarousel;
